import React, { useState } from "react";
import axios from "axios";
import { Plus, Calendar, Clock, Trash2, ExternalLink, X, Save } from "lucide-react";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Garage event calendar — add/edit/delete upcoming online events.
 * Stored on section.events.
 */
export default function EventCalendar({ section, isNight, onUpdated, asPopup = false, onClose }) {
    const accent = isNight ? "#D4AF37" : "#B89B5E";
    const { isAdmin } = useAdmin();
    const events = (section?.events || []).slice().sort((a, b) =>
        new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
    );

    const [editing, setEditing] = useState(null); // event obj or 'new'
    const [draft, setDraft] = useState({ title: "", datetime: "", description: "", url: "" });
    const [saving, setSaving] = useState(false);

    const openNew = () => {
        setDraft({ title: "", datetime: "", description: "", url: "" });
        setEditing("new");
    };

    const openEdit = (ev) => {
        setDraft({
            title: ev.title || "",
            datetime: ev.datetime ? ev.datetime.slice(0, 16) : "",
            description: ev.description || "",
            url: ev.url || "",
        });
        setEditing(ev);
    };

    const handleSave = async () => {
        if (!draft.title || !draft.datetime) return;
        setSaving(true);
        try {
            let nextEvents;
            if (editing === "new") {
                nextEvents = [
                    ...events,
                    {
                        id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`,
                        title: draft.title,
                        datetime: new Date(draft.datetime).toISOString(),
                        description: draft.description,
                        url: draft.url,
                    },
                ];
            } else {
                nextEvents = events.map((e) =>
                    e.id === editing.id
                        ? { ...e, title: draft.title, datetime: new Date(draft.datetime).toISOString(), description: draft.description, url: draft.url }
                        : e
                );
            }
            const res = await axios.patch(`${API}/sections/${section.id}`, { events: nextEvents });
            onUpdated && onUpdated(res.data);
            setEditing(null);
        } catch (e) { console.error(e); } finally { setSaving(false); }
    };

    const handleDelete = async (ev) => {
        try {
            const nextEvents = events.filter((e) => e.id !== ev.id);
            const res = await axios.patch(`${API}/sections/${section.id}`, { events: nextEvents });
            onUpdated && onUpdated(res.data);
        } catch (e) { console.error(e); }
    };

    const fmt = (iso) => {
        try {
            const d = new Date(iso);
            return d.toLocaleString(undefined, {
                weekday: "short", month: "short", day: "numeric",
                hour: "numeric", minute: "2-digit",
            });
        } catch (_) { return iso; }
    };

    return (
        <div
            data-testid="event-calendar"
            className={asPopup
                ? "fixed inset-0 z-40 flex items-center justify-center px-4 py-12 overflow-y-auto"
                : "absolute inset-0 z-10 overflow-y-auto px-4 pt-32 pb-16"}
            style={asPopup
                ? { background: "rgba(0,0,0,0.78)", backdropFilter: "blur(10px)" }
                : {
                    background: isNight
                        ? "radial-gradient(ellipse at top, #0d1018 0%, #02030a 70%)"
                        : "radial-gradient(ellipse at top, #f1ece0 0%, #d8d0bd 70%)",
                }}
            onClick={asPopup ? ((e) => { if (e.target === e.currentTarget) onClose && onClose(); }) : undefined}
        >
            <div
                className={asPopup
                    ? `${isNight ? "glass" : "glass-day"} relative rounded-2xl p-6 w-full max-w-2xl`
                    : "max-w-3xl mx-auto fade-in"}
                style={asPopup ? { border: `1px solid ${accent}55`, boxShadow: `0 30px 80px -20px ${accent}33` } : undefined}
            >
                {asPopup && onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="events-popup-close"
                        className="absolute top-4 right-4 w-9 h-9 rounded-full grid place-items-center z-10"
                        style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}55` }}
                    >
                        <X size={16} />
                    </button>
                )}
                <div className="text-center mb-10">
                    <div className="text-[10px] tracking-[0.5em] uppercase mb-3" style={{ color: accent }}>
                        Garage · Events
                    </div>
                    <h1
                        className="font-serif-display text-5xl md:text-7xl uppercase tracking-luxe leading-none"
                        style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                    >
                        Online Calendar
                    </h1>
                    <p className="mt-4 text-sm font-light max-w-md mx-auto" style={{ color: isNight ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)" }}>
                        Schedule and share upcoming live events you're hosting.
                    </p>
                </div>

                <div className="flex justify-end mb-4">
                    {isAdmin && (
                        <button
                            type="button"
                            onClick={openNew}
                            data-testid="event-add"
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase"
                            style={{ background: accent, color: "#0a0a0a" }}
                        >
                            <Plus size={14} /> Add Event
                        </button>
                    )}
                </div>

                <div className="space-y-3" data-testid="event-list">
                    {events.length === 0 && (
                        <div
                            className="text-center py-12 rounded-2xl"
                            style={{
                                background: isNight ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
                                border: `1px dashed ${accent}55`,
                                color: isNight ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
                            }}
                        >
                            <Calendar size={28} className="mx-auto mb-3 opacity-60" />
                            <p className="text-[11px] tracking-[0.3em] uppercase">No events scheduled</p>
                        </div>
                    )}
                    {events.map((ev) => (
                        <div
                            key={ev.id}
                            data-testid={`event-${ev.id}`}
                            className={`rounded-2xl p-5 ${isNight ? "glass" : "glass-day"}`}
                            style={{ border: `1px solid ${accent}55` }}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: accent }}>
                                        <Clock size={11} className="inline mr-1" />
                                        {fmt(ev.datetime)}
                                    </div>
                                    <h3
                                        className="font-serif-display text-2xl uppercase tracking-wide leading-tight mb-2"
                                        style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                                    >
                                        {ev.title}
                                    </h3>
                                    {ev.description && (
                                        <p
                                            className="text-sm font-light leading-relaxed mb-3"
                                            style={{ color: isNight ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)" }}
                                        >
                                            {ev.description}
                                        </p>
                                    )}
                                    {ev.url && (
                                        <a
                                            href={ev.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`event-link-${ev.id}`}
                                            className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase"
                                            style={{ color: accent }}
                                        >
                                            Open <ExternalLink size={11} />
                                        </a>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2 shrink-0">
                                    {isAdmin && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => openEdit(ev)}
                                                data-testid={`event-edit-${ev.id}`}
                                                className="text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full"
                                                style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}55` }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(ev)}
                                                data-testid={`event-delete-${ev.id}`}
                                                className="text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full inline-flex items-center gap-1"
                                                style={{
                                                    background: "transparent",
                                                    color: isNight ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                                                    border: `1px solid ${isNight ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
                                                }}
                                            >
                                                <Trash2 size={10} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Editor modal */}
            {editing && (
                <div
                    data-testid="event-editor"
                    className="fixed inset-0 z-50 flex items-center justify-center px-4 py-12 overflow-y-auto"
                    style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)" }}
                    onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
                >
                    <div className="glass rounded-2xl p-6 w-full max-w-md" style={{ border: `1px solid ${accent}55` }}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: accent }}>
                                {editing === "new" ? "Add Event" : "Edit Event"}
                            </div>
                            <button type="button" onClick={() => setEditing(null)} data-testid="event-editor-close" style={{ color: "rgba(255,255,255,0.7)" }}>
                                <X size={16} />
                            </button>
                        </div>
                        {[
                            ["title", "Title", "text"],
                            ["datetime", "Date & Time", "datetime-local"],
                            ["url", "URL (optional)", "url"],
                        ].map(([key, label, type]) => (
                            <React.Fragment key={key}>
                                <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mb-1" style={{ color: "#fff" }}>{label}</div>
                                <input
                                    type={type}
                                    value={draft[key]}
                                    onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                                    data-testid={`event-input-${key}`}
                                    className="w-full mb-3 rounded-xl px-4 py-2 outline-none text-sm"
                                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50`, colorScheme: "dark" }}
                                />
                            </React.Fragment>
                        ))}
                        <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mb-1" style={{ color: "#fff" }}>Description</div>
                        <textarea
                            rows={3}
                            value={draft.description}
                            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                            data-testid="event-input-description"
                            className="w-full mb-4 rounded-xl px-4 py-2 outline-none text-sm"
                            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                        />
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || !draft.title || !draft.datetime}
                            data-testid="event-save"
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[11px] tracking-[0.3em] uppercase disabled:opacity-50"
                            style={{ background: accent, color: "#0a0a0a" }}
                        >
                            <Save size={14} /> {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
